// SPEC-LF1-STORE-LOCAL-CACHE-01 (ARCH-LOCAL-FIRST-SYNC-V1 phase LF1):
// IndexedDB の thin wrapper。idb 8.x の openDB / transaction を最小限ラップして、
// patrol_records / store_meta の 2 object store を提供する。
//
// 設計判断:
// - DB 名: 'clawops_lf'  (LF1 phase の cache。LF2 で別 store 追加予定)
// - version 1 から開始。スキーマ拡張は version up + upgrade callback で migrate。
// - 1 booth 1 トランザクション原則は呼び出し側 (patrolRecords.js) が tx を保証する。

import { openDB } from 'idb'

export const DB_NAME = 'clawops_lf'
// SPEC-PATROL-ROUTE-BUILDER-01 (D-106): v2 で patrol_route_today ストア追加。
// SPEC-COLLECTION-OFFLINE-COMPLETE-01 (D-268) B1: v3 で collection_drafts ストア追加。
// D-268b (ADDENDUM_1 SCOPE_ADD_1): v4 で collection_read_cache と app_state を追加。v3 定義は書き換えず v4 で追加のみ。
export const DB_VERSION = 4

// object stores
export const STORE_PATROL_RECORDS = 'patrol_records' // 巡回 1 booth 1 record (in_meter 等)
export const STORE_STORE_META     = 'store_meta'     // 店舗メタ (storeCode -> { storeName, machines, prefetchedAt })
export const STORE_PATROL_ROUTE   = 'patrol_route_today' // 今日の巡回予定 (作業中の一時状態。日付JST付き1レコード)
// D-268 B1: 集金ドラフト(追記専用の送信箱)。keyPath=localId、index byStoreCode / bySynced('true'|'false')。
export const STORE_COLLECTION_DRAFTS = 'collection_drafts'
// D-268b SCOPE_ADD_1: 集金画面の読取キャッシュ(店舗単位)。keyPath=store_code。
//   中身: storeName / booths / machines / lastCollectionMeters / lastPatrolMeters / fetchedAt。
export const STORE_COLLECTION_READ_CACHE = 'collection_read_cache'
// D-268b SCOPE_ADD_1 / FIX_C11: 認証キャッシュ等のアプリ状態。keyPath='key'(店舗ストアに非店舗行を混ぜない)。
export const STORE_APP_STATE = 'app_state'

// FIX_C11: D-268a が store_meta に置いた予約行。v4 upgrade で削除する(本アプリが書いた行で巡回データではない)。
const LEGACY_AUTH_STAFF_KEY = '__auth_staff__'

let dbPromise = null

export function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      // D-268 B1/SCOPE_ADD_1: upgrade は追加のみ。既存 store の存在チェックを維持し、全て IF NOT EXISTS で
      //   v1/v2/v3/v4 のどこから来ても各ストアが揃うようにする。既存4ストアのスキーマ/データは不変。
      upgrade(db, oldVersion, _newVersion, tx) {
        if (!db.objectStoreNames.contains(STORE_PATROL_RECORDS)) {
          // key = localId (uuid) で put 可能。indexed by booth_code / store_code / synced。
          const ps = db.createObjectStore(STORE_PATROL_RECORDS, { keyPath: 'localId' })
          ps.createIndex('byBoothCode', 'booth_code', { unique: false })
          ps.createIndex('byStoreCode', 'store_code', { unique: false })
          // synced は 'true'|'false' 文字列。IndexedDB は boolean を index key にできないため文字列化。
          ps.createIndex('bySynced',    'syncedKey',  { unique: false })
        }
        if (!db.objectStoreNames.contains(STORE_STORE_META)) {
          db.createObjectStore(STORE_STORE_META, { keyPath: 'store_code' })
        }
        // D-106: 今日の巡回予定 (単一レコード keyPath 'id'。中身に stored_date(JST) と items)。
        if (!db.objectStoreNames.contains(STORE_PATROL_ROUTE)) {
          db.createObjectStore(STORE_PATROL_ROUTE, { keyPath: 'id' })
        }
        // D-268 B1: 集金ドラフト。巡回 patrol_records と同じ設計(localId key / byStoreCode / bySynced 文字列)を踏襲。
        if (!db.objectStoreNames.contains(STORE_COLLECTION_DRAFTS)) {
          const cs = db.createObjectStore(STORE_COLLECTION_DRAFTS, { keyPath: 'localId' })
          cs.createIndex('byStoreCode', 'store_code', { unique: false })
          cs.createIndex('bySynced',    'syncedKey',  { unique: false })
        }
        // D-268b SCOPE_ADD_1: 集金読取キャッシュ(店舗単位)。
        if (!db.objectStoreNames.contains(STORE_COLLECTION_READ_CACHE)) {
          db.createObjectStore(STORE_COLLECTION_READ_CACHE, { keyPath: 'store_code' })
        }
        // D-268b SCOPE_ADD_1: アプリ状態(認証キャッシュ等)。
        if (!db.objectStoreNames.contains(STORE_APP_STATE)) {
          db.createObjectStore(STORE_APP_STATE, { keyPath: 'key' })
        }
        // FIX_C11: v3 で store_meta に置いた __auth_staff__ 予約行を削除(app_state へ移設。巡回データではない)。
        if (oldVersion >= 3 && db.objectStoreNames.contains(STORE_STORE_META)) {
          try { tx.objectStore(STORE_STORE_META).delete(LEGACY_AUTH_STAFF_KEY) } catch { /* 無ければ無視 */ }
        }
      },
    })
  }
  return dbPromise
}

// テスト用。in-memory DB をリセットする (vitest 各 test で beforeEach から呼ぶ)。
export async function _resetDb() {
  if (dbPromise) {
    const db = await dbPromise
    db.close()
  }
  dbPromise = null
  if (typeof globalThis.indexedDB?.deleteDatabase === 'function') {
    await new Promise(resolve => {
      const req = globalThis.indexedDB.deleteDatabase(DB_NAME)
      req.onsuccess = resolve
      req.onerror = resolve
      req.onblocked = resolve
    })
  }
}
